import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Form,
  FormField,
  TextInput,
  Button,
  Select,
  Heading,
} from "grommet";
import {
  STATES_OPTIONS_LIST,
  PROVINCES_OPTIONS_LIST,
  getFilteredPropsObject,
} from "@common/utils";
import HotelService from "../../services/hotel.service";
import PhotoService from "../../services/photo.service";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { PhoneInput } from "@shared/";
import { PhotoUploader } from "./";
import { useFetchHotel } from "./redux/hooks";
import { DASHBOARD_FETCH_HOTEL_SUCCESS } from "./redux/constants";
import "./SettingsHotelInfo.scss";
import { useTranslation } from "react-i18next";
import { getOfferCapabilities } from "../../common/offerCapabilities";
import { PackageUpgradeModal } from "./components/OfferGate";
import EmailHeaderSettings from "./components/EmailHeaderSettings";
import { CustomTabs } from "../shared/CustomTabs";

const defaultValues = {
  name: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  country: "",
  email: "",
  phone: "",
  cover: "",
  logo: "",
  deposit: 200,
};

export default function SettingsHotelInfo(props) {
  const { t } = useTranslation();
  const { hotel, fetchHotel } = useFetchHotel();
  const dispatch = useDispatch();
  const previousHotel = useRef(null);
  const [formValues, setFormValues] = useState(defaultValues);
  const [upgrade, setUpgrade] = useState(false);
  const messaging = getOfferCapabilities(hotel).messaging;
  const isCanada = (formValues.country || "").toLowerCase() === "canada";
  const currentList = isCanada ? PROVINCES_OPTIONS_LIST : STATES_OPTIONS_LIST;
  const [statesOptions, setStatesOptions] = useState(currentList);

  useEffect(() => {
    setStatesOptions(currentList);
  }, [currentList, formValues.country]);

  useEffect(() => {
    const previous = previousHotel.current;
    previousHotel.current = hotel;
    if (previous && hotel && previous._id === hotel._id &&
        Object.keys(defaultValues).every(key => previous[key] === hotel[key])) {
      setFormValues(current => ({ ...hotel, ...Object.fromEntries(
        Object.keys(defaultValues).map(key => [key, current[key]]),
      ) }));
    } else {
      setFormValues(hotel || defaultValues);
    }
  }, [hotel]);

  const handleHeaderSaved = values => {
    setFormValues(current => ({ ...current, ...values }));
    dispatch({ type: DASHBOARD_FETCH_HOTEL_SUCCESS, data: { ...hotel, ...values } });
  };

  const handleCancel = () => {
    setFormValues(hotel || defaultValues);
  };

  function handleInputChange({ target: { name, value } }) {
    var values = { ...formValues };
    values[name] = value;
    setFormValues(values);
  }

  function handlePhoneInputChange(phone) {
    setFormValues({ ...formValues, phone });
  }

  const handleOnSubmit = (e) => {
    e.preventDefault();
    const request = getFilteredPropsObject(formValues, defaultValues);
    HotelService.updateHotel(request).then(
      (response) => {
        if (response.data.ok) {
          fetchHotel();
          presentToastSuccess("Information Updated");
        }
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const handleUploadCover = (file) => {
    HotelService.uploadCover(file).then(
      (response) => {
        if (response.data.success) {
          setFormValues({ ...formValues, cover: response.data.filename });
          presentToastSuccess("Photo Updated");
        }
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const handleUploadLogo = (file) => {
    HotelService.uploadLogo(file).then(
      (response) => {
        if (response.data.success) {
          setFormValues({ ...formValues, logo: response.data.filename });
          presentToastSuccess("Logo Updated");
        }
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  return (
    <Box fill align="center" justify="center">
      <Box width="large">
        <Heading alignSelf="start" level="2">
          {t("settingsInfo.title")}
        </Heading>
        {upgrade && <PackageUpgradeModal capability="messaging" onClose={() => setUpgrade(false)} />}
        <CustomTabs tabs={[
          { key: "information", title: t("emailAppearance.informationTab"), content: (<Form>
          <Box direction="row" justify="center">
            <PhotoUploader
              label={t("settingsInfo.coverPhoto")}
              onChange={handleUploadCover}
              fitWidth={980}
              fitHeight={600}
              photo={PhotoService.getURL(formValues.cover)}
            />
            <PhotoUploader
              blackbg
              label={t("settingsInfo.logo")}
              onChange={handleUploadLogo}
              fitWidth={400}
              fitHeight={400}
              photo={PhotoService.getURL(formValues.logo)}
            />
          </Box>
          <FormField label={t("settingsInfo.name")}>
            <TextInput
              name="name"
              value={formValues.name || ""}
              onChange={handleInputChange}
            />
          </FormField>
          <FormField label={t("settingsInfo.address")}>
            <TextInput
              name="address"
              value={formValues.address || ""}
              onChange={handleInputChange}
            />
          </FormField>
          <FormField label={t("settingsInfo.city")}>
            <TextInput
              name="city"
              value={formValues.city || ""}
              onChange={handleInputChange}
            />
          </FormField>
          <Box direction="row" gap="small">
            <FormField label={t("settingsInfo.country")} alignSelf="end">
              <TextInput
                name="country"
                value={formValues.country || ""}
                onChange={handleInputChange}
              />
            </FormField>
            <FormField
              label={t(
                isCanada ? "settingsInfo.province" : "settingsInfo.state",
              )}
            >
              <Select
                size="medium"
                labelKey="label"
                valueKey={{ key: "value", reduce: true }}
                placeholder={t(
                  isCanada
                    ? "settingsInfo.selectProvince"
                    : "settingsInfo.selectState",
                )}
                value={formValues.state || ""}
                options={statesOptions}
                onChange={({ value: nextValue }) =>
                  setFormValues({ ...formValues, state: nextValue })
                }
                onClose={() => setStatesOptions(currentList)}
                onSearch={(text) => {
                  const escapedText = text.replace(
                    /[-\\^$*+?.()|[\]{}]/g,
                    "\\$&",
                  );
                  const exp = new RegExp(escapedText, "i");
                  setStatesOptions(
                    currentList.filter(
                      (o) => exp.test(o.value) || exp.test(o.label),
                    ),
                  );
                }}
              />
            </FormField>
            <FormField label={t("settingsInfo.zipCode")} alignSelf="end">
              <TextInput
                name="zipCode"
                value={formValues.zipCode || ""}
                onChange={handleInputChange}
              />
            </FormField>
          </Box>
          <Box direction="row" gap="small">
            <FormField label={t("settingsInfo.email")} fill>
              <TextInput
                name="email"
                value={formValues.email || ""}
                onChange={handleInputChange}
                placeholder="example@my.com"
              />
            </FormField>
            <FormField
              label={t("settingsInfo.phone")}
              fill
              className="phone-field"
            >
              <PhoneInput
                name="phone"
                value={formValues.phone || ""}
                onChange={handlePhoneInputChange}
              />
            </FormField>
          </Box>

          <Box direction="row" justify="between" margin={{ top: "medium" }}>
            <Button label={t("settingsInfo.cancel")} onClick={handleCancel} />
            <Button
              type="submit"
              label={t("settingsInfo.update")}
              primary
              onClick={handleOnSubmit}
            />
          </Box>
        </Form>) },
          { key: "email", title: t("emailAppearance.title"), content: (
            <EmailHeaderSettings key={hotel?._id || "hotel"} hotel={formValues} enabled={messaging}
              onUpgrade={() => setUpgrade(true)} onSaved={handleHeaderSaved} />
          ) },
        ]} />
      </Box>
    </Box>
  );
}

SettingsHotelInfo.propTypes = {};
SettingsHotelInfo.defaultProps = {};
